import { defaultCheckError, IAction, IFetchParams, IRequestAction, progressEpic } from "epics/base-epic";
import { CUSTOMIZATION } from "constants/action-types";
import {
    CustomizationImageAttributeKind,
    ICustomizationTemplate,
    ILogoMerchantData,
    IDownloadURL,
    ImageTagStatusListName,
    LogoStatusType,
    IUploadProofBody,
    IUploadPayload,
    TCreateProofParams,
    type IBulkCustomizationQuery,
    ICheckCustomizationPayload,
} from "constants/product-customizations/customization";
import { TCustomizationSelectionImageAttribute } from "constants/product-customizations/customization-selection.types";
import { format, formatMessage } from "lib/globalize-util";
import siteConfig from "site-config";
import { ICompany } from "../components/contacts/contacts-types";
import me from "../models/me";
import { ajax } from "../lib/observable";
import { IProductCustomizationSettings } from "../records/brand/brand";
import { snackbarAdd } from "../actions/snackbars";
import _ from "lodash";
import { onAssignProof, onUploadProof } from "../actions/customization-actions";
import { getOrder } from "components/build-order";
import { PRODUCTS_FILTER } from "constants/brand-settings";
import { Observable } from "rxjs";

interface IProductTemplateRequestAction extends IRequestAction<{}> {
    templateId: string;
}

export interface ITime {
    seconds: number;
    nanos: number;
}

export interface ICustomizationImageEntityAttribute {
    name: string;
    stringValue: string;
    floatValue: number;
    boolValue: boolean;
    arrayValueList: string[];
    kind: CustomizationImageAttributeKind;
}

export interface ICustomizationImageEntity {
    id: string;
    merchantId: string;
    previewAssetId: string;
    name: string;
    url: string;
    uploadIdsList: Array<string>;
    forMerchantIdsList: Array<string>;
    attributesList?: ICustomizationImageEntityAttribute[];
    customizationAttributes?: TCustomizationSelectionImageAttribute[];
    updateTime?: ITime;
    createTime?: ITime;
    approvedMerchants?: ILogoMerchantData[];
    pendingMerchants?: ILogoMerchantData[];
    rejectedMerchants?: ILogoMerchantData[];
    downloadURL?: IDownloadURL;
}

interface ICreateImageRequestBody {
    name: string;
    kind: string;
    uploadId: string;
    additionalUploads: Array<string>;
}

interface ICreateImageRequestBody {
    templateId: string;
    imageIds: string[];
}

interface ICreateImageRequestAction extends IRequestAction<{}> {
    url: string;
    body: ICreateImageRequestBody;
}

interface IBulkCustomizationProductsAction extends IRequestAction<{}> {
    url: string;
    body: IBulkCustomizationQuery;
}

const getLogoUpdateMessage = (response, { payload }) => {
    const merchantId = payload?.body?.forMerchantId || "";
    const fileName = response?.name || "";
    const attributesMap = _.keyBy(response?.attributesList || [], "name");
    const approvedList = attributesMap[ImageTagStatusListName.APPROVED];
    const rejectedList = attributesMap[ImageTagStatusListName.REJECTED];
    const isApproved = approvedList?.arrayValueList.includes(merchantId);
    const isRejected = rejectedList?.arrayValueList.includes(merchantId);

    const status = isApproved ? LogoStatusType.APPROVED : isRejected ? LogoStatusType.REJECTED : "updated";
    return formatMessage("|fileName| has been |status|", {
        fileName,
        status,
    });
};

export const fetchCurrentProductTemplate = progressEpic<IProductTemplateRequestAction, ICustomizationTemplate>(
    CUSTOMIZATION.PRODUCT_TEMPLATES.FETCH,
    ({ payload }) => ({
        method: "GET",
        url: siteConfig.api + payload.url,
        withCredentials: true,
        crossDomain: true,
        retry: 2, // we don't need to retry this request lots of times in case of failure
    }),
    {
        debounce: 250,
        checkErrorStatus: ({ xhr }: { xhr?: XMLHttpRequest } = {}) => {
            if (xhr?.status === 404) {
                throw new Error(format("Template not found"));
            } else {
                defaultCheckError({ xhr });
            }
        },
    },
);

export const fetchBulkCustomizationProducts = progressEpic<IBulkCustomizationProductsAction, any>(
    CUSTOMIZATION.BULK_CUSTOMIZATIONS.PRODUCTS.FETCH,
    ({ payload }, state) => {
        const { scope } = state;
        const { brand } = scope;
        const { isPDP, ...body } = payload.body;
        const filters = { ...(body.filters ?? {}) };
        const order = getOrder(state.cart);

        const useOrderWarehouseFiltering =
            brand.hasPreference("set_warehouse_for_linesheets") &&
            brand.getPreference("filter_products_by") === PRODUCTS_FILTER.BY_WAREHOUSE_IN_WO;

        if (useOrderWarehouseFiltering && order?.selected_warehouse) {
            filters.__warehouses = [order.selected_warehouse];
        }

        return {
            method: "POST",
            url: siteConfig.api + payload.url,
            withCredentials: true,
            crossDomain: true,
            retry: 2,
            body: !isPDP
                ? body
                : {
                      ...body,
                      filters: {
                          ...filters,
                          currency_code: order?.currency_code,
                          __advanced_pricing_currency_code: scope.brand?.getAdvancedPricingCurrencyCode(scope),
                      },
                      activePricesheet: me._activePriceTemplate,
                      companies: scope.viewAs?._id ?? scope.company?._id,
                  },
        };
    },
    {
        dataCustomizer: (data) => {
            return data;
        },
        checkErrorStatus: defaultCheckError,
    },
);

export const createUploadImage = progressEpic<ICreateImageRequestAction, ICustomizationImageEntity>(
    CUSTOMIZATION.CREATE_CUSTOMIZATION_IMAGE.CREATE,
    ({ payload }) => {
        return {
            method: "POST",
            url: siteConfig.api + payload.url,
            withCredentials: true,
            crossDomain: true,
            body: payload.body,
        };
    },
    {
        checkErrorStatus: defaultCheckError,
    },
);

const customizationImagesBody = ({ payload }) => ({
    method: "POST",
    url: siteConfig.api + payload.url,
    withCredentials: true,
    crossDomain: true,
    body: payload.body,
});

const fetchImagesEpic = (type) =>
    progressEpic<IFetchParams, any>(type, customizationImagesBody, {
        checkErrorStatus: defaultCheckError,
    });

export const fetchLogoImagesEpic = fetchImagesEpic(CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.FETCH);
export const fetchExistingCustomizationImagesEpic = fetchImagesEpic(CUSTOMIZATION.LIST_MANAGE_LOGOS.FETCH);
export const checkCustomizationImagesEpic = fetchImagesEpic(CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.CHECK);

export interface IImageFetchParams extends IFetchParams {
    imageId?: string;
}

export const fetchExistingCustomizationImageDownloadURLEpic = progressEpic<IImageFetchParams, any>(
    CUSTOMIZATION.GET_CUSTOMIZATION_IMAGE_DOWNLOAD_URL.FETCH,
    ({ payload }) => ({
        method: "GET",
        url: siteConfig.api + payload.url,
        withCredentials: true,
        crossDomain: true,
    }),
    {
        checkErrorStatus: defaultCheckError,
        mapper: "mergeMap",
        dataCustomizer: (response, action) => ({ ...response, imageId: action.payload.imageId }),
    },
);

export const fetchBrandImageTagSchemasEpic = progressEpic<IImageFetchParams, any>(
    CUSTOMIZATION.IMAGE_TAG_SCHEMAS.BRAND.FETCH,
    ({ payload }) => ({
        method: "GET",
        url: siteConfig.api + payload.url,
        withCredentials: true,
        crossDomain: true,
    }),
    {
        checkErrorStatus: defaultCheckError,
    },
);

export const checkImageTabEpic = progressEpic<IFetchParams, any>(
    CUSTOMIZATION.LIST_CUSTOMIZATION_IMAGES.CHECK_IMAGE_TAB,
    customizationImagesBody,
    {
        checkErrorStatus: defaultCheckError,
    },
);

const PRODUCT_ACTION = CUSTOMIZATION.PRODUCT.FETCH;

const fetchCustomizableProductResult = (product) => [
    {
        type: PRODUCT_ACTION.SUCCESS,
        payload: { product },
    },
];

export const fetchCustomizationProductByIdEpic = (action$, store) => {
    return action$.ofType(PRODUCT_ACTION.START).switchMap(({ payload: id }) => {
        if (!id) {
            return Observable.of(fetchCustomizableProductResult(null));
        }

        const { scope } = store.getState();

        const body = {
            __advanced_pricing_currency_code: scope.brand?.getAdvancedPricingCurrencyCode(scope),
            __active_pricesheet: me._activePriceTemplate,
            __fields: "__all",
            __limit: 1000,
            _id: id,
        };

        return ajax({ url: `${siteConfig.api}/api/v3.0/query/items`, method: "POST", body }).flatMap(({ response }) =>
            fetchCustomizableProductResult(response?.data?.[0] ?? null),
        );
    });
};

export const fetchCompanyEpic = progressEpic<IRequestAction, ICompany>(CUSTOMIZATION.COMPANY.FETCH, ({ payload }) => ({
    method: "GET",
    url: siteConfig.api + payload.url,
}));

export const updateCustomizationBrandSettings = progressEpic<IFetchParams, IProductCustomizationSettings>(
    CUSTOMIZATION.BRAND_SETTINGS.UPDATE,
    ({ payload }) => {
        const { url, body } = payload;
        return {
            url: siteConfig.api + url,
            method: "POST",
            withCredentials: true,
            crossDomain: true,
            body,
        };
    },
    {
        checkErrorStatus: (resp) => {
            const { xhr, status } = resp;
            if (status === 400) {
                throw Error(
                    xhr?.response?.message || format("Oops! Something went wrong. Please refresh and try again."),
                );
            }
        },
    },
);

export const updateCustomizationLogo = progressEpic<IFetchParams, IProductCustomizationSettings>(
    CUSTOMIZATION.CUSTOMIZATION_IMAGE.UPDATE,
    ({ payload }) => {
        const { url, body } = payload;
        return {
            url: siteConfig.api + url,
            method: "PUT",
            withCredentials: true,
            crossDomain: true,
            body,
        };
    },
    {
        checkErrorStatus: (resp) => {
            const { xhr, status } = resp;
            if (status === 400) {
                throw Error(
                    xhr?.response?.message || format("Oops! Something went wrong. Please refresh and try again."),
                );
            }
        },
        finishActions: (response, action) => {
            const message = getLogoUpdateMessage(response, action);
            return [snackbarAdd({ message })];
        },
    },
);

export const checkCustomizationEpic = progressEpic<IFetchParams, IAction<ICheckCustomizationPayload>[]>(
    CUSTOMIZATION.ORDER.CHECK,
    ({ payload }) => {
        const { url } = payload;
        return {
            url: siteConfig.api + url,
            method: "GET",
            withCredentials: true,
            crossDomain: true,
        };
    },
    {
        checkErrorStatus: (resp) => {
            const { xhr, status } = resp;
            if (status === 400) {
                throw Error(
                    xhr?.response?.message || format("Oops! Something went wrong. Please refresh and try again."),
                );
            }
        },
    },
);

export const createProofEpic = progressEpic<TCreateProofParams, IAction<IUploadPayload>[]>(
    CUSTOMIZATION.PROOF.CREATE,
    ({ payload }) => {
        const { url, body } = payload;
        return {
            url: siteConfig.api + url,
            method: "POST",
            withCredentials: true,
            crossDomain: true,
            body,
        };
    },
    {
        checkErrorStatus: (resp) => {
            const { xhr, status } = resp;
            if (status === 400) {
                throw Error(
                    xhr?.response?.message || format("Oops! Something went wrong. Please refresh and try again."),
                );
            }
        },
        finishActions: (response, action) => {
            const data = response?.[0] || {};
            const requestData = data.upload;
            requestData.body = action.payload.file;

            return [onUploadProof(requestData)];
        },
    },
);

export const uploadProofEpic = progressEpic<IFetchParams, IAction<IUploadProofBody>>(
    CUSTOMIZATION.PROOF.UPLOAD,
    ({ payload }) => {
        const { method, url, headers, body } = payload || {};
        return {
            method,
            url,
            headers,
            body,
            withCredentials: false,
            crossDomain: true,
        };
    },
    {
        checkErrorStatus: (response) => {
            const { xhr } = response;
            throw Error(xhr.response?.localizedMessage);
        },
        finishActions: (response, action, state) => {
            const { orderId, lineItemId, payload } = state.customization.proofCreate;
            const body = {
                line_items: [
                    {
                        id: lineItemId,
                        uploadId: payload[0].upload.id,
                    },
                ],
            };
            return [onAssignProof(orderId, body)];
        },
    },
);

export const assignProofEpic = progressEpic<IFetchParams, null>(
    CUSTOMIZATION.PROOF.ASSIGN,
    ({ payload }) => {
        const { url, body } = payload;

        return {
            url: siteConfig.api + url,
            method: "PUT",
            withCredentials: true,
            crossDomain: true,
            body,
        };
    },
    {
        checkErrorStatus: (resp) => {
            const { xhr, status } = resp;
            if (status === 400) {
                throw Error(
                    xhr?.response?.message || format("Oops! Something went wrong. Please refresh and try again."),
                );
            }
        },
    },
);
